import { Fragment, useRef, useState, type ReactNode } from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import type { FormNode } from '../../designer/types/form.types'
import { resolveRuntimeFieldVisible } from '@/shared/hooks/useRuntimeFieldVisibility'
import { validatePagesTree } from '@/shared/form/pagesModel'
import { tabularCaptionSx } from '@/shared/components/TabularSectionTitle'

export interface PagesElementProps {
  node: FormNode
  renderNode: (n: FormNode) => ReactNode
  rowCounts?: Record<string, number>
  formValues?: Record<string, unknown>
}

function pageBadgeCount(page: FormNode, rowCounts?: Record<string, number>): number | undefined {
  const kids = page.children ?? []
  if (kids.length !== 1 || kids[0].type !== 'Table') return undefined
  const count = rowCounts?.[page.name]
  return typeof count === 'number' ? count : undefined
}

export function PagesElement({ node, renderNode, rowCounts, formValues }: PagesElementProps) {
  const [active, setActive] = useState(0)
  const warnedRef = useRef(false)
  const errors = validatePagesTree(node)
  if (errors.length > 0 && !warnedRef.current) {
    warnedRef.current = true
    console.warn(errors.join(' '))
  }

  if (errors.length > 0) {
    return (
      <>
        {(node.children ?? []).map((child) => (
          <Fragment key={child.id}>{renderNode(child)}</Fragment>
        ))}
      </>
    )
  }

  const pages = (node.children ?? []).filter(
    (c) =>
      c.type === 'Page' &&
      resolveRuntimeFieldVisible(
        { visible: c.visible, visibleCondition: c.visibleCondition },
        formValues,
      ),
  )
  if (pages.length === 0) return null
  const safeIndex = Math.min(active, pages.length - 1)
  const activePage = pages[safeIndex]

  return (
    <Box
      sx={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Tabs
        value={safeIndex}
        onChange={(_, v: number) => setActive(v)}
        sx={{
          minHeight: 36,
          minWidth: 0,
          flexShrink: 0,
          '& .MuiTabs-indicator': { display: 'none' },
          '& .MuiTab-root': {
            minHeight: 36,
            minWidth: 0,
            py: 0.5,
            px: 0,
            mr: 2.5,
            textTransform: 'none',
            opacity: 1,
            color: 'text.primary',
            alignItems: 'flex-start',
          },
        }}
      >
        {pages.map((page, i) => {
          const count = pageBadgeCount(page, rowCounts)
          const label = page.title || page.name
          const selected = i === safeIndex
          return (
            <Tab
              key={page.id}
              disableRipple
              label={
                <Box
                  data-tabular-page-label
                  data-active={selected ? 'true' : 'false'}
                  sx={{
                    ...tabularCaptionSx,
                    mb: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.75,
                    borderColor: selected ? 'primary.main' : 'transparent',
                  }}
                >
                  {label}
                  {count != null ? (
                    <Chip size="small" label={count} sx={{ height: 18, fontSize: 11 }} />
                  ) : null}
                </Box>
              }
            />
          )
        })}
      </Tabs>
      {activePage ? (
        <Box sx={{ pt: 1, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {renderNode(activePage)}
        </Box>
      ) : null}
    </Box>
  )
}
